import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  MousePointerClick, Mail, Store, Handshake, Mic, Sparkles, DollarSign,
  Download, LogOut, Megaphone,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { toast } from "sonner";

const STAT_META = [
  { key: "ticket_clicks", label: "Ticket Clicks", icon: MousePointerClick, color: "#00f0ff" },
  { key: "leads", label: "Newsletter Leads", icon: Mail, color: "#ffe600" },
  { key: "vendors", label: "Vendor Apps", icon: Store, color: "#ff0055", sub: "vendors_paid", subLabel: "paid" },
  { key: "sponsors", label: "Sponsor Inquiries", icon: Handshake, color: "#00f0ff" },
  { key: "performers", label: "Performer Subs", icon: Mic, color: "#ffe600" },
  { key: "runway", label: "Runway Registrations", icon: Sparkles, color: "#ff0055" },
];

const STATUS_COLORS = { Pending: "#ffe600", Approved: "#00f0ff", Declined: "#ff0055" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [ann, setAnn] = useState({ enabled: false, message: "" });

  const loadSubs = useCallback(async () => {
    const kind = filter === "all" ? "" : filter;
    const { data } = await api.get(`/admin/submissions${kind ? `?kind=${kind}` : ""}`);
    setSubs(data);
  }, [filter]);

  useEffect(() => {
    api.get("/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/announcement").then((r) => setAnn(r.data)).catch(() => {});
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/submissions/${id}`, { status });
      setSubs((s) => s.map((x) => (x.id === id ? { ...x, status, payment_status: data.payment_status ?? x.payment_status } : x)));
      const note = status === "Approved" && data.payment_status === "paid" ? " — card charged" : status === "Declined" && data.payment_status === "released" ? " — hold released" : "";
      toast.success(`Marked ${status}${note}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update");
    }
  };

  const saveAnnouncement = async (next) => {
    setAnn(next);
    await api.put("/admin/announcement", next);
    toast.success("Announcement updated");
  };

  const exportCsv = async (kind) => {
    const res = await fetch(`${API}/admin/export${kind ? `?kind=${kind}` : ""}`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${kind || "all"}_submissions.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const name = (s) => s.businessName || s.companyName || s.name || "—";

  return (
    <div className="min-h-screen bg-[#08080c]">
      <header className="bg-[#101018]/95 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="font-display text-2xl chromatic">MISSION CONTROL</div>
          <div className="flex items-center gap-4">
            <span className="font-head text-sm text-white/60 hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={doLogout} className="inline-flex items-center gap-2 text-white/70 hover:text-[#ff0055] font-head text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8 space-y-8">
        {/* METRICS */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {STAT_META.map((m) => (
            <div key={m.key} className="glass-chroma rounded-2xl p-5" data-testid={`metric-${m.key}`}>
              <m.icon size={20} style={{ color: m.color }} />
              <div className="font-display text-4xl mt-3">{metrics?.[m.key] ?? "—"}</div>
              <div className="font-head text-xs text-white/50 uppercase tracking-wider mt-1">{m.label}</div>
              {m.sub && <div className="font-mono2 text-[10px] text-[#00f0ff] mt-1">{metrics?.[m.sub] ?? 0} {m.subLabel}</div>}
            </div>
          ))}
        </div>

        {/* REVENUE + ANNOUNCEMENT */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass-chroma rounded-2xl p-6 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#00f0ff]/15 grid place-items-center"><DollarSign className="text-[#00f0ff]" /></div>
            <div>
              <div className="font-head text-xs uppercase tracking-wider text-white/50">Confirmed Revenue (Stripe)</div>
              <div className="font-display text-4xl text-[#00f0ff]">${(metrics?.revenue ?? 0).toLocaleString()}</div>
            </div>
          </div>
          <div className="glass-chroma rounded-2xl p-6" data-testid="announcement-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 font-head font-bold"><Megaphone size={18} className="text-[#ffe600]" /> Site Announcement</div>
              <Switch data-testid="announcement-toggle" checked={ann.enabled} onCheckedChange={(v) => saveAnnouncement({ ...ann, enabled: v })} />
            </div>
            <div className="flex gap-2">
              <input
                data-testid="announcement-message"
                value={ann.message}
                onChange={(e) => setAnn((a) => ({ ...a, message: e.target.value }))}
                placeholder="Only 50 VIP Passes Remaining!"
                className="flex-1 bg-[#08080c] border border-white/15 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#ffe600]"
              />
              <button data-testid="announcement-save" onClick={() => saveAnnouncement(ann)} className="bg-[#ffe600] text-black font-head font-bold uppercase text-sm px-4 rounded-lg hover:scale-105 transition-transform">Save</button>
            </div>
          </div>
        </div>

        {/* SUBMISSIONS */}
        <div className="glass-chroma rounded-2xl p-6">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
            <div className="flex gap-2">
              {["all", "vendor", "sponsor", "performer"].map((k) => (
                <button
                  key={k}
                  data-testid={`filter-${k}`}
                  onClick={() => setFilter(k)}
                  className={`font-head text-sm uppercase px-4 py-2 rounded-full transition-colors ${filter === k ? "bg-[#ff0055] text-white" : "bg-[#08080c] text-white/60 hover:text-white"}`}
                >
                  {k}
                </button>
              ))}
            </div>
            <button data-testid="export-csv" onClick={() => exportCsv(filter === "all" ? "" : filter)} className="inline-flex items-center gap-2 glass font-head text-sm uppercase px-4 py-2 rounded-full hover:border-[#00f0ff]/50"><Download size={16} /> Export CSV</button>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/50">Name</TableHead>
                  <TableHead className="text-white/50">Type</TableHead>
                  <TableHead className="text-white/50">Email</TableHead>
                  <TableHead className="text-white/50">Payment</TableHead>
                  <TableHead className="text-white/50">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subs.length === 0 && (
                  <TableRow className="border-white/10"><TableCell colSpan={5} className="text-center text-white/40 font-head py-8">No submissions yet.</TableCell></TableRow>
                )}
                {subs.map((s) => (
                  <TableRow key={s.id} className="border-white/10 hover:bg-white/5" data-testid={`sub-row-${s.id}`}>
                    <TableCell className="font-head font-semibold">{name(s)}{s.tier ? <span className="block text-xs text-white/40">{s.tier}</span> : null}</TableCell>
                    <TableCell className="font-mono2 text-xs uppercase text-white/60">{s.kind}</TableCell>
                    <TableCell className="font-head text-sm text-white/70">{s.email}</TableCell>
                    <TableCell>
                      <span className={`font-mono2 text-xs px-2 py-1 rounded-full ${s.payment_status === "paid" ? "bg-[#00f0ff]/15 text-[#00f0ff]" : s.payment_status === "held" ? "bg-[#ffe600]/15 text-[#ffe600]" : s.payment_status === "released" ? "bg-[#ff0055]/15 text-[#ff0055]" : "bg-white/5 text-white/40"}`}>{s.payment_status === "held" ? "hold · approve to charge" : s.payment_status}</span>
                    </TableCell>
                    <TableCell>
                      <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
                        <SelectTrigger data-testid={`status-select-${s.id}`} className="w-32 h-9 bg-[#08080c] border-white/15" style={{ color: STATUS_COLORS[s.status] }}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#101018] border-white/10 text-white">
                          {["Pending", "Approved", "Declined"].map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
